import { useEffect, useRef, useState } from "react";
import { Sparkles, Package, LayoutTemplate } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { AccountOut, TemplateOut, TemplatesPage } from "../types";
import { BannerCarousel } from "./BannerCarousel";
import { TemplateNumberSearch } from "./TemplateNumberSearch";

const SUGGESTED_COUNT = 10;
const AUTO_ADVANCE_MS = 3200;

function SuggestedTemplates({ onPick }: { onPick: (template: TemplateOut) => void }) {
  const [templates, setTemplates] = useState<TemplateOut[] | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const first = await api.get<TemplatesPage>(`/api/templates?page=0`);
      let page = Math.max(0, first.total_pages - 1);
      let collected: TemplateOut[] = page === 0 ? first.items : [];
      // اجمع من آخر صفحة وارجع للخلف لين يكتمل العدد (آخر صفحة ممكن تحتوي قالب واحد فقط)
      while (page > 0 && collected.length < SUGGESTED_COUNT) {
        const res = await api.get<TemplatesPage>(`/api/templates?page=${page}`);
        collected = [...res.items, ...collected];
        page -= 1;
      }
      if (page === 0 && collected.length < SUGGESTED_COUNT && first.total_pages > 1) {
        collected = [...first.items, ...collected];
      }
      if (!cancelled) setTemplates(collected.slice(-SUGGESTED_COUNT));
    };
    load()
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!templates || templates.length < 2) return;
    const timer = setInterval(() => {
      if (pausedRef.current) return;
      const track = trackRef.current;
      if (!track) return;
      indexRef.current = (indexRef.current + 1) % templates.length;
      const card = track.children[indexRef.current] as HTMLElement | undefined;
      card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [templates]);

  if (!templates || templates.length === 0) return null;

  return (
    <div>
      <span className="flex items-center gap-1.5 text-sm font-bold text-tg-text mb-2.5">
        <LayoutTemplate size={15} strokeWidth={2.25} className="text-tg-hint" />
        قوالب مقترحة
      </span>
      <div
        ref={trackRef}
        onTouchStart={() => {
          pausedRef.current = true;
        }}
        onTouchEnd={() => {
          window.setTimeout(() => {
            pausedRef.current = false;
          }, 2500);
        }}
        className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory pb-1"
        style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch" }}
      >
        {templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onPick(tpl)}
            className="snap-start shrink-0 w-24 rounded-xl overflow-hidden bg-white/5 border border-white/10 text-start"
          >
            <img src={tpl.preview_url} alt={tpl.name} loading="lazy" className="w-full aspect-square object-cover" />
            <span className="block text-[11px] text-tg-hint truncate px-1.5 py-1">{tpl.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AccountScreen({ onPickTemplate }: { onPickTemplate: (template: TemplateOut) => void }) {
  const [account, setAccount] = useState<AccountOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalTemplates, setTotalTemplates] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<AccountOut>("/api/account")
      .then(setAccount)
      .catch((err) => setError(err instanceof ApiError ? err.message : "تعذر تحميل بيانات الحساب"));
    api
      .get<TemplatesPage>("/api/templates?page=0")
      .then((res) => setTotalTemplates(res.total))
      .catch(() => undefined);
  }, []);

  if (error) {
    return (
      <div className="h-full overflow-y-auto p-5">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="h-full overflow-y-auto p-5">
        <p className="text-tg-hint text-sm">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 flex flex-col gap-5">
      <BannerCarousel />

      <TemplateNumberSearch onPickTemplate={onPickTemplate} />

      <SuggestedTemplates onPick={onPickTemplate} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="glass-card p-2.5 flex flex-col items-center gap-0.5">
          <Package size={16} className="text-vinyl-violet" strokeWidth={2.25} />
          <span className="text-xl font-extrabold tabular-nums">{account.packs_count}</span>
          <span className="text-[10px] text-tg-hint">حزمة منشأة</span>
        </div>

        <div className="glass-card p-2.5 flex flex-col items-center gap-0.5">
          <Sparkles size={16} className="text-vinyl-bubblegum" strokeWidth={2.25} />
          <span className="text-xl font-extrabold tabular-nums">{account.stickers_count}</span>
          <span className="text-[10px] text-tg-hint">ملصق مصمم</span>
        </div>

        <div className="glass-card p-2.5 flex flex-col items-center gap-0.5">
          <LayoutTemplate size={16} className="text-tg-hint" strokeWidth={2.25} />
          <span className="text-xl font-extrabold tabular-nums">{totalTemplates ?? "–"}</span>
          <span className="text-[10px] text-tg-hint">إجمالي القوالب</span>
        </div>
      </div>
    </div>
  );
}
